import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { HeaderComponent } from '../../shared/components/header/header.component';
import { ConsumerSidebarComponent } from '../sidebar/consumer-sidebar.component';
import { PageHeaderComponent } from '../../shared/components/page-header/page-header.component';
import { AuthApiService } from '@angular-workspace/services';
import { ConfirmationDialogComponent } from '../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { ToasterService } from '../../services/toaster.service';

@Component({
  selector: 'app-consumer-join-organisation',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    HeaderComponent,
    ConsumerSidebarComponent,
    PageHeaderComponent,
    ConfirmationDialogComponent,
  ],
  templateUrl: './join-organisation.component.html',
  styleUrl: './join-organisation.component.scss',
})
export class ConsumerJoinOrganisationComponent implements OnInit {
  @ViewChild('sidebar') sidebarComponent!: ConsumerSidebarComponent;
  
  joinOrganisationForm!: FormGroup;
  organisationNames: string[] = [];
  organisations: any[] = []; // Store full organisation data with IDs
  filteredOrganisations: string[] = [];
  showOrganisationDropdown = false;
  loading = false;
  submitting = false;
  organisationStatus: 'pending' | 'granted' | null = null;
  userOrganisation: any = null;
  isPendingStatus = false;
  showSuccessModal = false;
  showWithdrawConfirm = false;
  withdrawing = false;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private authApiService: AuthApiService,
    private toasterService: ToasterService
  ) {}

  async ngOnInit(): Promise<void> {
    this.initForm();
    await this.loadUserInfo();
    await this.loadOrganisations(); // Await to ensure organisations are loaded before populating
    this.setupOrganisationFilter();

    // Landed after withdraw (Organisation page navigated here with ?withdrawn=1) – show Join form, clear URL
    if (this.route.snapshot.queryParamMap.get('withdrawn') === '1') {
      this.isPendingStatus = false;
      this.userOrganisation = null;
      this.organisationStatus = null;
      this.joinOrganisationForm.get('entityName')?.enable();
      this.joinOrganisationForm.get('jobTitle')?.enable();
      this.joinOrganisationForm.get('employeeId')?.enable();
      this.joinOrganisationForm.patchValue({ entityName: '', jobTitle: '', employeeId: '' });
      this.router.navigate([], { relativeTo: this.route, queryParams: { withdrawn: null }, queryParamsHandling: 'merge' });
    }

    // If pending status, disable all fields and populate from user API
    if (this.isPendingStatus && this.userOrganisation) {
      await this.populateFormFromOrganisation();
    }
  }
  
  async populateFormFromOrganisation(): Promise<void> {
    if (!this.userOrganisation) return;
    
    // Get organisationId from user API response
    const userOrgId = this.userOrganisation.organisationId || this.userOrganisation['organisationId'] || this.userOrganisation['organizationId'] || null;
    
    // Find the organisation from getAllOrganisations API by matching organisationId with id
    let orgName = '';
    if (userOrgId && this.organisations.length > 0) {
      const matchedOrg = this.organisations.find(org => 
        org.id === userOrgId || 
        org['id'] === userOrgId ||
        org.organisationId === userOrgId ||
        org['organisationId'] === userOrgId
      );
      
      if (matchedOrg) {
        orgName = matchedOrg.orgName || matchedOrg['orgName'] || matchedOrg.name || matchedOrg['name'] || '';
      }
    }
    
    // Fallback to orgName from userOrganisation if not found in organisations list
    if (!orgName) {
      orgName = this.userOrganisation.orgName || this.userOrganisation['name'] || '';
    }
    
    const jobTitle = this.userOrganisation.jobTitle || this.userOrganisation['jobTitle'] || this.userOrganisation['job_title'] || '';
    // Map empId from API response to employeeId field
    const employeeId = this.userOrganisation.empId || this.userOrganisation['empId'] || this.userOrganisation.employeeId || this.userOrganisation['employeeId'] || this.userOrganisation['emp_id'] || '';
    
    // Disable all fields
    this.joinOrganisationForm.get('entityName')?.disable();
    this.joinOrganisationForm.get('jobTitle')?.disable();
    this.joinOrganisationForm.get('employeeId')?.disable();
    
    // Populate values
    if (orgName) {
      this.joinOrganisationForm.patchValue({ entityName: orgName });
    }
    if (jobTitle) {
      this.joinOrganisationForm.patchValue({ jobTitle: jobTitle });
    }
    if (employeeId) {
      this.joinOrganisationForm.patchValue({ employeeId: employeeId });
    }
  }
  
  setupOrganisationFilter(): void {
    const entityNameControl = this.joinOrganisationForm.get('entityName');
    if (entityNameControl) {
      entityNameControl.valueChanges.subscribe(value => {
        this.filterOrganisations(value || '');
      });
    }
  }
  
  filterOrganisations(searchValue: string): void {
    const filterValue = searchValue.toLowerCase();
    if (filterValue === '') {
      this.filteredOrganisations = this.organisationNames;
    } else {
      this.filteredOrganisations = this.organisationNames.filter(org =>
        org.toLowerCase().includes(filterValue)
      );
    }
    this.showOrganisationDropdown = this.filteredOrganisations.length > 0;
  }
  
  selectOrganisation(orgName: string): void {
    this.joinOrganisationForm.patchValue({ entityName: orgName });
    this.showOrganisationDropdown = false;
  }

  /**
   * True if the current organisation name value is empty or matches an organisation in the platform list (case-insensitive).
   */
  isOrganisationInList(): boolean {
    const value = (this.joinOrganisationForm.get('entityName')?.value ?? '').toString().trim();
    if (value === '') return true;
    return this.organisations.some(
      (org: any) => (org?.orgName ?? '').toString().toLowerCase().trim() === value.toLowerCase()
    );
  }

  /**
   * True when the user has entered a non-empty organisation name that is not in the platform list.
   */
  showOrganisationNotInListMessage(): boolean {
    const control = this.joinOrganisationForm.get('entityName');
    const value = (control?.value ?? '').toString().trim();
    if (value === '' || !(control?.dirty || control?.touched)) return false;
    return !this.isOrganisationInList();
  }

  /**
   * True when the user has chosen an organisation that is present in the list (show "Organisation available").
   */
  showOrganisationAvailableMessage(): boolean {
    const control = this.joinOrganisationForm.get('entityName');
    const value = (control?.value ?? '').toString().trim();
    if (value === '' || !(control?.dirty || control?.touched)) return false;
    return this.isOrganisationInList();
  }
  
  onOrganisationInputFocus(): void {
    if (this.organisationNames.length > 0) {
      this.filteredOrganisations = this.organisationNames;
      this.showOrganisationDropdown = true;
    }
  }
  
  onOrganisationInputBlur(): void {
    // Delay hiding dropdown to allow click events to fire
    setTimeout(() => {
      this.showOrganisationDropdown = false;
    }, 200);
  }
  
  async loadUserInfo(): Promise<void> {
    try {
      const userInfo = await this.authApiService.getUserInfo();
      
      // The API response may have a 'result' object containing the actual user data
      const result = userInfo['result'] || userInfo;
      
      // Extract user name and email from response
      // API response structure may vary, so we check common property names
      const userName = result['name'] || result['userName'] || result['fullName'] || result['displayName'] || result['firstName'] || '';
      const userEmail = result['email'] || result['emailAddress'] || result['registeredEmail'] || result['emailId'] || '';
      
      // Prepopulate the form fields
      if (userName) {
        this.joinOrganisationForm.patchValue({ userName });
      }
      if (userEmail) {
        this.joinOrganisationForm.patchValue({ registeredEmail: userEmail });
      }
      
      // Check organisation status and extract organisation data
      const org = result['organisation'] || result['organization'] || result['org'] || null;
      
      if (org && org.status) {
        this.userOrganisation = org;
        this.organisationStatus = org.status === 'pending' || org.status === 'granted' ? org.status : null;
        this.isPendingStatus = org.status === 'pending';
      }
    } catch (error) {
      console.error('Error loading user info:', error);
      // Keep empty values if API fails (fields remain disabled)
    }
  }
  
  async loadOrganisations(): Promise<void> {
    this.loading = true;
    try {
      const response = await this.authApiService.getAllOrganisations(1, 500);
      
      // Extract orgName from response
      let organisations: any[] = [];
      if (response && response['result'] && Array.isArray(response['result'])) {
        organisations = response['result'];
      } else if (Array.isArray(response)) {
        organisations = response;
      }
      
      // Store full organisation data
      this.organisations = organisations;
      
      // Extract unique organisation names
      this.organisationNames = organisations
        .map(org => org.orgName)
        .filter((name): name is string => name != null && name !== '')
        .filter((name, index, self) => self.indexOf(name) === index)
        .sort();
    } catch (error) {
      console.error('Error loading organisations:', error);
      this.organisationNames = [];
    } finally {
      this.loading = false;
    }
  }
  
  initForm(): void {
    this.joinOrganisationForm = this.fb.group({
      entityName: ['', [Validators.required]],
      registeredEmail: [{ value: '', disabled: true }],
      userName: [{ value: '', disabled: true }],
      jobTitle: ['', [Validators.required]],
      employeeId: ['', [Validators.required]]
    });
  }
  
  async onSubmit(): Promise<void> {
    if (this.joinOrganisationForm.valid && this.isOrganisationInList()) {
      this.submitting = true;
      try {
        const formValue = this.joinOrganisationForm.getRawValue();
        const selectedOrgName = (formValue.entityName ?? '').toString().trim();
        
        // Find the organisation ID from the selected organisation name (case-insensitive)
        const selectedOrganisation = this.organisations.find(
          (org: any) => (org?.orgName ?? '').toString().trim().toLowerCase() === selectedOrgName.toLowerCase()
        );
        
        if (!selectedOrganisation || !selectedOrganisation.id) {
          this.toasterService.error('Organisation is not available.');
          this.submitting = false;
          return;
        }
        
        // Build the payload as per API specification
        const payload = {
          job_title: formValue.jobTitle,
          emp_id: formValue.employeeId,
          official_email: formValue.registeredEmail
        };
        
        // Call the joinOrganisation API
        const response = await this.authApiService.joinOrganisation(selectedOrganisation.id, payload);
        
        console.log('Join organisation request submitted successfully:', response);
        
        // Show success modal
        this.showSuccessModal = true;
        
      } catch (error) {
        console.error('Error submitting join organisation request:', error);
        // TODO: Show error message to user
      } finally {
        this.submitting = false;
      }
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.joinOrganisationForm.controls).forEach(key => {
        this.joinOrganisationForm.get(key)?.markAsTouched();
      });
    }
  }
  
  onCancel(): void {
    console.log('Form cancelled');
    this.joinOrganisationForm.reset();
    // TODO: Navigate back or close the form
  }
  
  isFieldInvalid(fieldName: string): boolean {
    const field = this.joinOrganisationForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }
  
  onCreateNewEntity(): void {
    console.log('Navigating to create organisation');
    this.router.navigate(['/consumer/create-organisation']);
  }
  
  onSuccessModalClose(): void {
    this.showSuccessModal = false;
    // Optionally navigate to home or refresh the page
    // this.router.navigate(['/consumer/home']);
  }
  
  async   onSuccessModalConfirm(): Promise<void> {
    this.showSuccessModal = false;
    // Refresh sidebar to show "Awaiting Approval" notification
    if (this.sidebarComponent) {
      await this.sidebarComponent.refreshOrganisationStatus();
    }
    // Navigate to organisation page
    this.router.navigate(['/consumer/data-provider-entity']);
  }

  onWithdrawClick(): void {
    this.showWithdrawConfirm = true;
  }

  onWithdrawConfirmClose(): void {
    this.showWithdrawConfirm = false;
  }

  async onWithdrawConfirm(): Promise<void> {
    if (!this.userOrganisation) {
      this.toasterService.error('Unable to withdraw: organisation data not found.');
      this.showWithdrawConfirm = false;
      return;
    }
    const requestId = this.userOrganisation.requestId ?? this.userOrganisation.request_id ?? this.userOrganisation.joinRequestId ?? this.userOrganisation.join_request_id ?? this.userOrganisation.id;
    if (!requestId) {
      this.toasterService.error('Unable to withdraw: request details not found. Please try again later.');
      this.showWithdrawConfirm = false;
      return;
    }
    this.withdrawing = true;
    try {
      await this.authApiService.withdrawOrganisationRequest(requestId);
      this.toasterService.success('Your request has been withdrawn successfully.');
      this.showWithdrawConfirm = false;
      this.isPendingStatus = false;
      this.userOrganisation = null;
      this.organisationStatus = null;
      // Re-enable form and clear organisation-related fields so user can submit a new request
      this.joinOrganisationForm.get('entityName')?.enable();
      this.joinOrganisationForm.get('jobTitle')?.enable();
      this.joinOrganisationForm.get('employeeId')?.enable();
      this.joinOrganisationForm.patchValue({ entityName: '', jobTitle: '', employeeId: '' });
      if (this.sidebarComponent) {
        await this.sidebarComponent.refreshOrganisationStatus(true);
      }
    } catch (error) {
      console.error('Error withdrawing request:', error);
      this.toasterService.error('Unable to withdraw request. Please try again later.');
    } finally {
      this.withdrawing = false;
    }
  }
}

